export class ApiError extends Error {
  constructor(
    public readonly status: number,
    /** The API's error code — see `packages/shared/src/errors.ts`. `http_<status>` when the body carried none. */
    public readonly code: string,
    public readonly details?: unknown,
    /** Seconds to wait, from `details.retryAfterSeconds` or the `Retry-After` header on a 429. */
    public readonly retryAfter?: number,
  ) {
    super(code);
    this.name = 'ApiError';
  }
}

const BASE = import.meta.env.VITE_API_URL ?? '';
const ACT_AS_KEY = 'wasity.actAs';

/**
 * Admin oversight: while "acting as" a broker or valuer, broker/evaluator calls carry their id and the
 * API records every action as done by the admin on their behalf. Kept per tab (sessionStorage).
 */
export const actAs = {
  get(): { id: string; name: string; role: 'broker' | 'evaluator' } | null {
    try {
      const raw = sessionStorage.getItem(ACT_AS_KEY);
      return raw ? (JSON.parse(raw) as { id: string; name: string; role: 'broker' | 'evaluator' }) : null;
    } catch {
      return null;
    }
  },
  set(v: { id: string; name: string; role: 'broker' | 'evaluator' } | null) {
    try {
      if (v) sessionStorage.setItem(ACT_AS_KEY, JSON.stringify(v));
      else sessionStorage.removeItem(ACT_AS_KEY);
    } catch {
      /* private mode */
    }
  },
};

async function request<T>(method: string, path: string, body?: unknown, extra: Record<string, string> = {}): Promise<T> {
  const acting = actAs.get();
  const headers: Record<string, string> = { 'content-type': 'application/json', ...extra };
  if (acting && /^\/api\/(broker|evaluator)\b/.test(path)) headers['x-act-as-user'] = acting.id;
  const res = await fetch(`${BASE}${path}`, {
    method,
    credentials: 'include',
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const text = await res.text();
  // A body that is not JSON is a proxy or a gateway answering, not the API: a 502's HTML page, a plain
  // «Not Found». Parsing it must not throw a SyntaxError past every `instanceof ApiError` branch there is.
  let data: Record<string, unknown> = {};
  try {
    if (text) data = JSON.parse(text) as Record<string, unknown>;
  } catch {
    if (res.ok) throw new ApiError(res.status, 'bad_response');
  }
  if (!res.ok) {
    // The session died while the panel was open — expired, revoked, or signed out in another tab.
    // Without this the cached `me` kept every guard satisfied, so clicking a nav link left the person
    // on a broker page reading «انتهت جلستك» beside a Retry button that could never succeed. The
    // sign-in endpoints answer 401 for a wrong password and must not be read as a lost session.
    if (res.status === 401 && !path.startsWith('/api/auth/') && path !== '/api/me') sessionLost?.();
    const details = data.details as { retryAfterSeconds?: unknown } | undefined;
    const header = res.headers.get('retry-after') ?? res.headers.get('x-retry-after');
    const retry = typeof details?.retryAfterSeconds === 'number' ? details.retryAfterSeconds : header ? Number(header) : NaN;
    throw new ApiError(res.status, typeof data.error === 'string' ? data.error : `http_${res.status}`, data.details, Number.isFinite(retry) ? Math.max(1, Math.ceil(retry)) : undefined);
  }
  return data as T;
}

/**
 * What to do when a request comes back unauthenticated. Registered once by the app so this module can
 * stay free of React and of the query client.
 */
let sessionLost: (() => void) | undefined;
export const onSessionLost = (fn: () => void) => {
  sessionLost = fn;
};

export const api = {
  get: <T>(path: string, extra?: Record<string, string>) => request<T>('GET', path, undefined, extra),
  post: <T>(path: string, body?: unknown, extra?: Record<string, string>) => request<T>('POST', path, body ?? {}, extra),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body ?? {}),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body ?? {}),
  del: <T>(path: string) => request<T>('DELETE', path),
};

/** Query string from a filter object, skipping empty values. */
export function qs(params: Record<string, string | number | undefined | null>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== '') p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : '';
}

/**
 * Direct-to-storage upload: presign, PUT the bytes, confirm. With the mock storage (local dev, CI) the
 * presigned URL is not a real endpoint, so the PUT is skipped and the row is confirmed as-is.
 */
export async function uploadFile(presignPath: string, file: File, purpose: string): Promise<{ mediaId: string; publicUrl: string | null }> {
  const p = await api.post<{ mediaId: string; upload: { url: string; method: 'PUT'; headers: Record<string, string> }; publicUrl: string | null }>(`${presignPath}/presign`, { purpose, filename: file.name, contentType: file.type || 'application/octet-stream', sizeBytes: file.size });
  if (!p.upload.url.startsWith('mock://')) {
    const put = await fetch(p.upload.url, { method: p.upload.method, headers: p.upload.headers, body: file });
    if (!put.ok) throw new ApiError(put.status, 'upload_failed');
  }
  await api.post(`${presignPath}/${p.mediaId}/complete`);
  return { mediaId: p.mediaId, publicUrl: p.publicUrl };
}
